import { MDSIcon, MDSIconKind } from "@/mds";
import { ThemeType } from "@/mds/themes/theme-context";
import { THEME_LABEL } from "@/mds/themes/theme-constants";
import { useThemeSelection } from "@/domains/theme/use-theme-selection";
import { MDSMenuItem } from "@/mds/components/menu/menu-item";
import {
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
} from "@/mds/components/menu/menu-item/menu-item.types";
import { StyledMenuItem } from "@/mds/components/menu/action-menu/action-menu.style";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { SubmenuTrigger, Menu, MenuItem, Popover } from "react-aria-components";

interface SidebarDropdownThemeSwitcherProps {
  onSelectTheme?: () => void;
}

export const SidebarDropdownThemeSwitcher = observer(function SidebarDropdownThemeSwitcher({
  onSelectTheme,
}: SidebarDropdownThemeSwitcherProps) {
  const { themePreference, selectTheme } = useThemeSelection();
  const { store } = useAppStore();

  const handleSelectTheme = (theme: ThemeType) => {
    onSelectTheme?.();
    selectTheme(theme);
  };

  // Hide on mobile/touch — the submenu pattern doesn't work on touch devices.
  // Users can still change the theme via Settings > Appearance.
  if (store.interface.isTouchDevice) return null;

  return (
    <SubmenuTrigger>
      <StyledMenuItem
        data-mds-name="action-menu-item"
        id="theme-switcher"
        aria-label="Theme"
        textValue="Theme"
      >
        <MDSMenuItem
          id="theme-switcher"
          label="Theme"
          icon={MDSIconKind.Moon}
          variant={MDSMenuItemVariant.OneLine}
          rightAddon={() => (
            <StyledThemeLabel>
              {THEME_LABEL[themePreference]}
              <MDSIcon kind={MDSIconKind.AngleRight} size={10} />
            </StyledThemeLabel>
          )}
        />
      </StyledMenuItem>
      <StyledSubmenuPopover placement="end top" offset={0} data-test-id="theme-submenu-popover">
        <StyledSubmenu aria-label="Theme options">
          <MenuItem
            id={ThemeType.Harvest}
            textValue="Light"
            onAction={() => handleSelectTheme(ThemeType.Harvest)}
          >
            <MDSMenuItem
              id={ThemeType.Harvest}
              label="Light"
              icon={MDSIconKind.SunBright}
              isSelected={themePreference === ThemeType.Harvest}
              selectionVariant={
                themePreference === ThemeType.Harvest
                  ? MDSMenuItemSelectionVariant.Checkmark
                  : undefined
              }
              variant={MDSMenuItemVariant.OneLine}
            />
          </MenuItem>
          <MenuItem
            id={ThemeType.HarvestMoon}
            textValue="Dark"
            onAction={() => handleSelectTheme(ThemeType.HarvestMoon)}
          >
            <MDSMenuItem
              id={ThemeType.HarvestMoon}
              label="Dark"
              icon={MDSIconKind.Moon}
              isSelected={themePreference === ThemeType.HarvestMoon}
              selectionVariant={
                themePreference === ThemeType.HarvestMoon
                  ? MDSMenuItemSelectionVariant.Checkmark
                  : undefined
              }
              variant={MDSMenuItemVariant.OneLine}
            />
          </MenuItem>
          <MenuItem
            id={ThemeType.System}
            textValue="System"
            onAction={() => handleSelectTheme(ThemeType.System)}
          >
            <MDSMenuItem
              id={ThemeType.System}
              label="System"
              icon={MDSIconKind.Desktop}
              isSelected={themePreference === ThemeType.System}
              selectionVariant={
                themePreference === ThemeType.System
                  ? MDSMenuItemSelectionVariant.Checkmark
                  : undefined
              }
              variant={MDSMenuItemVariant.OneLine}
            />
          </MenuItem>
        </StyledSubmenu>
      </StyledSubmenuPopover>
    </SubmenuTrigger>
  );
});

const StyledSubmenuPopover = styled(Popover)`
  width: 176px;
  padding-block: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.surface.menu};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`;

const StyledSubmenu = styled(Menu)`
  min-width: 176px;
`;

const StyledThemeLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
`;
